<template>
  <div class="login-container">
    <div class="left-form">
      <img src="@/assets/images/common/drip-logo.svg" />
      <h1 class="heading">{{ $t('newForgotPassword.heading') }}</h1>
      <p class="mb-0">{{ $t('newForgotPassword.subHeading') }}</p>
      <div class="mb-5 mt-2">
        <span class="mr-2">Or</span>
        <router-link
          :to="{ name: 'login' }"
          class="link font-weight-bold"
          id="back-to-login"
        >
          {{ $t('newForgotPassword.loginLink') }}
        </router-link>
      </div>

      <b-form @submit.prevent="sendResetLink" data-vv-scope="forgotpassword">
        <div class="status-card-container">
          <error-card
            :error="error"
            :text="errorText.text"
            :description="errorText.description"
          />
          <success-card
            :success="success"
            :text="$t('newForgotPassword.success.text')"
          />
        </div>
        <div class="form-container mt-2">
          <div class="form-field mb-4" v-if="!success">
            <h3 class="label">
              {{ $t('newForgotPassword.forgotForm.emailLabel') }}
            </h3>
            <input
              type="email"
              class="input-field"
              :class="{ 'input-error': errors.first('forgotpassword.email') }"
              :placeholder="$t('newForgotPassword.forgotForm.emailPlaceholder')"
              v-model="formData.email"
              @blur="setEmailToState"
              @input="handleInput"
              v-validate="'required|email'"
              data-vv-as="Email"
              data-vv-validate-on="input"
              data-vv-scope="forgotpassword"
              name="email"
            />
            <div
              class="mt-2 error-block"
              v-if="errors.first('forgotpassword.email')"
            >
              <span class="error">
                <b-icon-x-circle
                  v-if="errors.first('forgotpassword.email')"
                  variant="danger"
                />
                {{ errors.first('forgotpassword.email') }}
              </span>
            </div>
          </div>
          <div v-if="!success">
            <action-button variant="solid" type="submit" extraClass="w-100">
              <b-icon-arrow-repeat
                v-if="loading"
                animation="spin"
                class="mr-2 color-white"
              />
              {{ $t('newForgotPassword.forgotForm.resetButton') }}
            </action-button>
            <p class="mt-5">
              {{ $t('newForgotPassword.forgotForm.info') }}
            </p>
          </div>
          <div v-if="success" class="color-noble-blue-500">
            <div class="drip-label font-weight-400">
              {{ $t('confirmEmail.link1_1') }}
              <strong>{{ formData.email || 'email-id' }}</strong>
              {{ $t('confirmEmail.link1_2') }}
            </div>
            <div class="spacing-16"></div>
            <div class="d-flex align-items-center">
              <div class="drip-label font-weight-bold color-noble-blue-500">
                {{ $t('confirmEmail.link2_question') }}
              </div>
              <a
                href="/forgot-password"
                class="drip-body-small font-weight-bold d-block ml-2 hover-on-underline"
              >
                {{ $t('confirmEmail.link2_link') }}
              </a>
            </div>
            <div class="spacing-32"></div>
            <div class="drip-body-small font-weight-400">
              {{ $t('confirmEmail.info') }}
            </div>
            <div class="d-flex">
              <action-button
                variant="text"
                class="drip-body-small font-weight-bold px-4"
                :click="resendEmail"
              >
                {{ $t('confirmEmail.resend') }}
              </action-button>
            </div>
            <div class="spacing-32"></div>
            <div class="d-flex align-items-center">
              <div
                class="drip-body-small font-weight-bold color-noble-blue-500"
              >
                {{ $t('confirmEmail.link3_question') }}
              </div>
              <a
                href="/login"
                class="d-flex align-items-center drip-body-small font-weight-bold ml-2 hover-on-underline"
              >
                {{ $t('confirmEmail.link3_link') }}
                <b-icon-chevron-right class="ml-1" />
              </a>
            </div>
          </div>
        </div>
      </b-form>
    </div>
    <image-container />
  </div>
</template>
<script>
import ErrorCard from '@/components/validation/ErrorCard'
import SuccessCard from '@/components/validation/SuccessCard'
import { setEmailToStateFn } from '@/helpers/authentications'
import userService from '@/services/user'
import ImageContainer from './ImageContainer.vue'
import ActionButton from '@/components/common/ActionButton'

export default {
  data() {
    return {
      success: false,
      loading: false,
      publicToken: '',
      formData: {
        email: this.$store.state.account.email || ''
      },
      error: false
    }
  },
  components: {
    ErrorCard,
    SuccessCard,
    ImageContainer,
    ActionButton
  },
  mounted() {
    document.title = 'Forgot Password | Drip Portal'
    this.getPublicToken()
  },
  methods: {
    getPublicToken() {
      return userService
        .fetchAccessToken()
        .then((response) => {
          this.publicToken = response.data.token
        })
        .catch((err) => {
          console.log(err)
        })
    },
    sendResetLink() {
      this.$validator
        .validate()
        .then((result) => {
          if (result) {
            this.loading = true
            userService
              .forgotPassword({
                user: this.formData,
                api_token: this.publicToken
              })
              .then((res) => {
                this.$mpTrack('Reset password Success', {
                  email: this.formData.email
                })
                this.loading = false
                this.success = true
              })
              .catch((err) => {
                this.loading = false
                this.error = true
                if (
                  err &&
                  err.response &&
                  err.response.data &&
                  err.response.data.message &&
                  err.response.data.message.length &&
                  err.response.data.message.length > 0
                ) {
                  this.$mpTrack('Reset password Failed', {
                    email: this.formData.email,
                    error: err.response.data.message[0]
                  })
                } else {
                  this.$mpTrack('Reset password Failed', {
                    email: this.formData.email,
                    error: 'Some error occurred'
                  })
                }
                return this.$notify({
                  title: this.$t('notifications.passwordError.title'),
                  text: this.$t('notifications.passwordError.text'),
                  type: 'error'
                })
              })
          }
        })
        .catch((e) => {
          console.log('e', e)
        })
    },
    setEmailToState() {
      setEmailToStateFn(this.formData.email)
    },
    resendEmail(e) {
      e.preventDefault()
      this.success = false
      this.sendResetLink()
    },
    handleInput() {
      this.error = false
    }
  },
  computed: {
    errorText() {
      return {
        text: this.$t('newForgotPassword.error.text'),
        description: `${this.$t('newForgotPassword.error.description', {
          email: this.formData.email
        })}`
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.form-container,
.status-card-container {
  width: 75% !important;
  @media screen and (max-width: 1024px) {
    width: 90% !important;
  }
  @media screen and (max-width: 728px) {
    width: 100% !important;
  }
}
</style>
